import { Show, createSignal } from "solid-js";
import { fileToSourceData, textToSourceData } from "../file-source";
import type { FileSourceData } from "../stores/diff";
import { Modal } from "./Modal";

export type DropZoneMode = "file" | "text";

interface DropZoneProps {
  onSubmit: (left: FileSourceData, right: FileSourceData) => void | Promise<void>;
  // Optional external paste-state: when provided, the DropZone reads its
  // initial paste text / mode from these accessors and writes back via the
  // callbacks. Lets the parent persist paste state across unmount.
  pasteLeft?: () => string;
  pasteRight?: () => string;
  mode?: () => DropZoneMode;
  onPasteLeftChange?: (v: string) => void;
  onPasteRightChange?: (v: string) => void;
  onModeChange?: (m: DropZoneMode) => void;
}

type Mode = DropZoneMode;

function compareForAssignment(a: File, b: File): number {
  if (a.lastModified !== b.lastModified) return a.lastModified - b.lastModified;
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

// Densely-populated Unicode emoji blocks. Some code points inside are
// unassigned or platform-specific so the occasional pick may render as
// tofu; the pool is large enough that it's rare and the surprise is part
// of the charm.
const EMOJI_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x1F600, 0x1F64F], // Emoticons (😀-🙏)
  [0x1F680, 0x1F6C5], // Transport & map (🚀-🛅)
  [0x1F910, 0x1F9FF], // Supplemental faces & objects
  [0x1F400, 0x1F4FC], // Animals & objects
  [0x1F300, 0x1F3FA], // Weather, sky, places
  [0x1FA70, 0x1FAFF], // Symbols & pictographs extended-A
];

function randomEmoji(): string {
  const range = EMOJI_RANGES[Math.floor(Math.random() * EMOJI_RANGES.length)] as readonly [number, number];
  const [lo, hi] = range;
  const cp = lo + Math.floor(Math.random() * (hi - lo + 1));
  return String.fromCodePoint(cp);
}

function pickTwoDistinctEmojis(): [string, string] {
  const a = randomEmoji();
  let b = randomEmoji();
  while (b === a) b = randomEmoji();
  return [a, b];
}

export function DropZone(props: DropZoneProps) {
  // Mode and paste text are initialized from props (if given) so the parent
  // can persist them across DropZone unmount/remount. Each setter also
  // mirrors to the parent via callbacks.
  const [mode, setModeSig] = createSignal<Mode>(props.mode?.() ?? "file");
  const setMode = (m: Mode) => { setModeSig(m); props.onModeChange?.(m); };

  const [leftEmoji, rightEmoji] = pickTwoDistinctEmojis();

  // Drop-tab state
  const [leftFile, setLeftFile] = createSignal<FileSourceData | null>(null);
  const [rightFile, setRightFile] = createSignal<FileSourceData | null>(null);
  const [dragOver, setDragOver] = createSignal(false);

  // Paste-tab state (independent of Drop tab) — initialized from optional
  // external accessors so it survives unmount when the parent retains them.
  const [leftText, setLeftTextSig] = createSignal<string>(props.pasteLeft?.() ?? "");
  const [rightText, setRightTextSig] = createSignal<string>(props.pasteRight?.() ?? "");
  const setLeftText  = (s: string) => { setLeftTextSig(s);  props.onPasteLeftChange?.(s); };
  const setRightText = (s: string) => { setRightTextSig(s); props.onPasteRightChange?.(s); };

  // Error modal
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  const assignFiles = async (files: File[]) => {
    const sorted = [...files].sort(compareForAssignment);
    const olderFile = sorted[0]!;
    const newerFile = sorted[1]!;
    const [l, r] = await Promise.all([
      fileToSourceData(olderFile),
      fileToSourceData(newerFile),
    ]);
    setLeftFile(l);
    setRightFile(r);
  };

  const handleFiles = (files: File[]) => {
    if (files.length === 1) {
      setLeftFile(null);
      setRightFile(null);
      setErrorMsg("ファイルを 2 つドロップしてください（1 つ受け取りました）");
      return;
    }
    if (files.length > 2) {
      setLeftFile(null);
      setRightFile(null);
      setErrorMsg(`ファイルを 2 つだけドロップしてください（${files.length} つ受け取りました）`);
      return;
    }
    void assignFiles(files);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    handleFiles(files);
  };

  const onPick = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    handleFiles(files);
    target.value = ""; // allow re-picking same files
  };

  const swap = () => {
    const l = leftFile();
    const r = rightFile();
    if (!l || !r) return;
    setLeftFile(r);
    setRightFile(l);
  };

  const dropReady = () => leftFile() !== null && rightFile() !== null;
  const textReady = () => leftText().length > 0 && rightText().length > 0;
  const ready = () => (mode() === "file" ? dropReady() : textReady());

  const submit = async () => {
    if (mode() === "file") {
      const l = leftFile();
      const r = rightFile();
      if (!l || !r) return;
      await props.onSubmit(l, r);
    } else {
      if (!textReady()) return;
      await props.onSubmit(
        textToSourceData(leftText(), ""),
        textToSourceData(rightText(), ""),
      );
    }
  };

  return (
    <div class="fd-dropzone">
      <div class="fd-dropzone-tabs">
        <button
          type="button"
          class={`fd-dropzone-tab ${mode() === "file" ? "fd-dropzone-tab-active" : ""}`}
          onClick={() => setMode("file")}
        >
          ファイルを比較
        </button>
        <button
          type="button"
          class={`fd-dropzone-tab ${mode() === "text" ? "fd-dropzone-tab-active" : ""}`}
          onClick={() => setMode("text")}
        >
          貼り付けて比較
        </button>
      </div>

      <Show when={mode() === "file"}>
        <div
          class={`fd-dropzone-main ${dragOver() ? "fd-dropzone-dragover" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <p class="fd-dropzone-hint">比較するファイルをまとめてドロップするか、ダイアログから選択してください</p>
          <div class="fd-dropzone-slots">
            <Slot side="left"  label={leftEmoji}  data={leftFile()} />
            <button
              type="button"
              class="fd-slot-swap"
              aria-label="swap left and right"
              disabled={!dropReady()}
              onClick={swap}
            >
              ⇄
            </button>
            <Slot side="right" label={rightEmoji} data={rightFile()} />
          </div>
          <div style={{ "text-align": "center" }}>
            <label class="fd-dropzone-pick">
              ファイルを選択
              <input
                type="file"
                multiple
                onChange={onPick}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>
      </Show>

      <Show when={mode() === "text"}>
        <div class="fd-dropzone-paste">
          <textarea
            aria-label="paste left text"
            placeholder="ここにテキストを貼り付けてください"
            value={leftText()}
            onInput={(e) => setLeftText(e.currentTarget.value)}
          />
          <textarea
            aria-label="paste right text"
            placeholder="ここにテキストを貼り付けてください"
            value={rightText()}
            onInput={(e) => setRightText(e.currentTarget.value)}
          />
        </div>
      </Show>

      <div class="fd-dropzone-actions">
        <button
          type="button"
          disabled={!ready()}
          onClick={submit}
          class="fd-button-primary"
        >
          比較開始
        </button>
      </div>

      <Modal
        open={errorMsg() !== null}
        title="エラー"
        onClose={() => setErrorMsg(null)}
      >
        <p>{errorMsg()}</p>
      </Modal>
    </div>
  );
}

function Slot(p: {
  side: "left" | "right";
  label: string;
  data: FileSourceData | null;
}) {
  return (
    <div class="fd-slot" data-side={p.side} aria-label={`${p.side} slot`}>
      <div class="fd-slot-primary">
        <span class="fd-slot-label">{p.label}</span>
        <Show
          when={p.data}
          fallback={<span class="fd-slot-empty">ファイルが指定されていません</span>}
        >
          {(d) => <span class="fd-slot-name">{d().name}</span>}
        </Show>
      </div>
      <Show when={p.data}>
        {(d) => <span class="fd-slot-meta">{d().bytes.length} bytes</span>}
      </Show>
    </div>
  );
}
